import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import CallTestPatientStatusPanel from './CallTestPatientStatusPanel';
import { AutomatedPatientIframe } from '@components/AutomatedPatientIframe';
import { connect, WindowMessenger } from 'penpal';
import ServiceApi from 'vl-common/src/services/service.api';
import type {
  ParentAutomatedParticipantAPI,
  BearerToken,
  TwilioTestToken,
  ParticipantLogEntry
} from '../../../lib/automatedParticipant/types';
import TestCallInstructions from './TestCallInstructions';
import { CallManager } from '@helpers/CallManager';
import breakpoints from '@lib/breakpoints';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../contexts/NotificationContext/NotificationContext';
import useMediaPermissions from '@hooks/useMediaPermissions';

export type TestCallStep =
  | 'waiting_for_prompt'
  | 'ready_to_record'
  | 'recording'
  | 'ready_to_replay'
  | 'replayed'
  | 'complete';

export type SetTestCallStepType = React.Dispatch<React.SetStateAction<TestCallStep>>;

const CallTestSession = () => {
  const { t } = useTranslation();
  const { errorNotification } = useNotification();
  const { checkCurrentPermissions } = useMediaPermissions();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const penpalConnectionRef = useRef<ReturnType<typeof connect<ParentAutomatedParticipantAPI>> | null>(null);
  const [callCompleted, setCallCompleted] = useState(false);
  const [showCallTestModal, setShowCallTestModal] = useState(false);
  const [testCallSignalingManager, setTestCallSignalingManager] = useState<CallManager | null>(null);
  const [testCallStep, setTestCallStep] = useState<TestCallStep>('waiting_for_prompt');
  const [isBlockedByMedia, setIsBlockedByMedia] = useState(false);

  const showNotification = (message: string) => {
    const DESCRIPTION = t('Please refresh the page and try again.');
    const DURATION = 10;

    if (message.includes('missing audio or video track')) {
      errorNotification({
        message: t('Unable to record'),
        description: DESCRIPTION,
        duration: DURATION
      });
    } else if (message === 'Failed to play clip') {
      errorNotification({
        message: t('Unable to play clip'),
        description: DESCRIPTION,
        duration: DURATION
      });
    }
  };

  useEffect(() => {
    const getMediaPermissions = async () => {
      const { audio, video } = await checkCurrentPermissions();
      setIsBlockedByMedia(!audio || !video);
    };
    getMediaPermissions();
  }, []);

  useEffect(() => {
    if (globalThis.window === undefined || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const setupPenpal = async () => {
      if (!iframe.contentWindow) {
        const onLoad = () => {
          if (iframe.contentWindow) {
            setupPenpal();
          }
        };
        iframe.addEventListener('load', onLoad);
        return () => iframe.removeEventListener('load', onLoad);
      }

      try {
        const connection = connect<ParentAutomatedParticipantAPI>({
          messenger: new WindowMessenger({
            remoteWindow: iframe.contentWindow,
            allowedOrigins: [globalThis.window.location.origin]
          }),
          methods: {
            getVlBearerToken: async (): Promise<BearerToken> => {
              const { Auth } = await import('aws-amplify');
              const session = await Auth.currentSession();
              const idToken = session.getIdToken();
              const token = idToken.getJwtToken();
              const expiration = 'getExpiration' in idToken ? idToken.getExpiration() * 1000 : Date.now() + 3600000;
              return {
                token: `Bearer ${token}`,
                expiresAt: expiration
              };
            },
            getTwilioTestToken: async (): Promise<TwilioTestToken> => {
              const guid = ServiceApi.userGuid;
              if (!guid) {
                throw new Error('Clinician GUID not available');
              }

              const tokenResponse = await ServiceApi.get('/videotokengen', {
                params: { appointment_id: undefined, user_guid: guid, automated: true }
              });
              const token = tokenResponse?.data?.token;
              const roomName = tokenResponse?.data?.roomName;
              if (!token || !roomName) {
                throw new Error('Missing video token or room name');
              }
              return {
                token,
                roomName,
                expiresAt: Date.now() + 3600000
              };
            },
            logEvent: async (entry: ParticipantLogEntry): Promise<void> => {
              const contextStr =
                entry.context && Object.keys(entry.context).length > 0 ? ` ${JSON.stringify(entry.context)}` : '';
              console.log(`[AutomatedPatient][${entry.level.toUpperCase()}] ${entry.message}${contextStr}`);

              if (['error', 'warn'].includes(entry.level)) showNotification(entry.message);
            },
            getParticipantConfig: async (): Promise<Record<string, unknown> | undefined> => {
              return undefined;
            }
          }
        });

        penpalConnectionRef.current = connection;
        await connection.promise;
      } catch (error) {
        console.error('Failed to setup Penpal parent bridge for test call:', error);
      }
    };

    setupPenpal();

    return () => {
      if (penpalConnectionRef.current) {
        penpalConnectionRef.current.destroy();
        penpalConnectionRef.current = null;
      }
    };
  }, []);

  return (
    <div
      css={css`
        display: flex;

        @media (max-width: ${breakpoints.md}px) {
          flex-direction: column;
        }
      `}
    >
      <AutomatedPatientIframe ref={iframeRef} />
      <div
        css={css`
          flex-shrink: 0;
        `}
      >
        <CallTestPatientStatusPanel
          setTestCallSignalingManager={setTestCallSignalingManager}
          setTestCallStep={setTestCallStep}
          testCallStep={testCallStep}
          setCallCompleted={setCallCompleted}
          callCompleted={callCompleted}
          isBlockedByMedia={isBlockedByMedia}
        />
      </div>
      <div
        css={css`
          width: 70%;
          min-height: fit-content;
          background-color: #f6f6f6;
          padding: 10px 0 4rem 20px;
          flex: 1;
        `}
      >
        <TestCallInstructions
          testCallSignalingManager={testCallSignalingManager}
          setTestCallStep={setTestCallStep}
          testCallStep={testCallStep}
          setCallCompleted={setCallCompleted}
          setShowCallTestModal={setShowCallTestModal}
          showCallTestModal={showCallTestModal}
          isBlockedByMedia={isBlockedByMedia}
        />
      </div>
    </div>
  );
};

export default CallTestSession;
